import React from 'react';
import { styled } from '@compiled/react';
import { DatePicker } from '@atlaskit/datetime-picker';
import { Label } from '@atlaskit/form';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { extractDate } from '@atlassian/jira-software-releases-version-date-utils/src/common/utils/extract-date.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { FIRST_COLUMN_MIN_WIDTH } from '../../../common/constants';
import { DatePickerReadView } from '../../../common/ui/version-dates';
import messages from './messages';
import { convertDatetimeToDate } from './utils';

const INPUT_NAME = 'version-details-editable-date-picker';

export type Props = {
	analyticsEventName: string;
	canEdit: boolean | null;
	currentDate: string | null;
	displayDate: string;
	label: string;
	maxDate?: string;
	minDate?: string;
	onUpdateDate: (date: string | null) => void;
	testId?: string;
};

export const EditableDatePicker = ({
	analyticsEventName,
	canEdit,
	currentDate,
	displayDate,
	label,
	maxDate,
	minDate,
	onUpdateDate,
	testId,
}: Props) => {
	const locale = useLocale();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const defaultValue = currentDate != null ? extractDate(currentDate) : null;

	return (
		<Container data-testid={testId !== undefined ? `${testId}-container` : undefined}>
			{canEdit === true ? (
				<InlineEdit
					defaultValue={defaultValue}
					editView={({ ...fieldProps }) => (
						<DatePicker
							autoFocus
							locale={locale}
							defaultValue={defaultValue || undefined}
							maxDate={maxDate}
							minDate={minDate}
							placeholder={formatMessage(messages.selectDateLabel)}
							spacing="compact"
							testId={testId}
							{...fieldProps}
						/>
					)}
					label={label}
					onConfirm={(newValue: string) => {
						if (convertDatetimeToDate(newValue) !== convertDatetimeToDate(currentDate)) {
							onUpdateDate(newValue ? new Date(newValue).toISOString() : null);
						}
					}}
					onEdit={() => {
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'datePicker',
							}),
							analyticsEventName,
						);
					}}
					readView={() => (
						<DatePickerReadView
							displayDate={displayDate}
							testId={testId !== undefined ? `${testId}-read-view` : undefined}
						/>
					)}
					readViewFitContainerWidth
				/>
			) : (
				<Box xcss={noEditContainerStyles}>
					<Label htmlFor={INPUT_NAME}>{label}</Label>
					<InlineEditReadViewContainer>
						<DatePickerReadView
							displayDate={displayDate}
							testId="software-releases-launch-card.ui.version-dates.editable-date-picker.date-picker-read-view"
						/>
					</InlineEditReadViewContainer>
				</Box>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	['data-testid']?: string;
	children: React.ReactNode;
}>({
	flex: 1,
	marginTop: token('space.negative.100', '-8px'),
	marginBottom: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		minWidth: `${FIRST_COLUMN_MIN_WIDTH}px`,
	},
});

const noEditContainerStyles = xcss({
	marginTop: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InlineEditReadViewContainer = styled.div({
	border: '2px solid transparent',
	lineHeight: 1,
});
